import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Flex, Form, Input, Result, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  authorizationV2InviteAcceptCreateMutation,
  authorizationV2InviteInfoRetrieveOptions
} from 'src/api/main'
import { ContentSpinner } from 'src/components'
import { paths } from 'src/router'
import { useInlineApiErrors } from 'src/utils'

import { AuthHeader, AuthLink } from '../components'

const { Text } = Typography

type InvitationFormValues = {
  first_name: string
  last_name: string
  password: string
  confirm_password: string
}

/**
 * Where the `?token=` link in a workspace invitation email lands — the URL
 * `buildWorkspaceInviteUrl` hands to the backend, which until now had no page on
 * this side at all.
 *
 * Two outcomes, decided by `has_password` on the invite info. Someone who already
 * has an account only has to sign in: the backend creates the `OrganizationAccess`
 * row when the invite is sent, so the workspace is already waiting for them. A new
 * person sets their name and password here, and accepting is what turns the
 * placeholder the invite created into an account they can log into.
 *
 * Ported from `ebs-platform-web`'s `InvitationForm`, which owns this flow in the
 * other frontend, and re-fitted to this app's auth-page idioms.
 */
export const Invitation = () => {
  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const navigate = useNavigate()
  const { t } = useTranslation()
  const { message } = App.useApp()
  const [form] = Form.useForm<InvitationFormValues>()
  const { showApiErrors, errorAlert } = useInlineApiErrors()

  const {
    data: invite,
    isLoading,
    isError
  } = useQuery({
    ...authorizationV2InviteInfoRetrieveOptions({ query: { token } }),
    enabled: Boolean(token),
    // An expired, revoked or already-accepted token is a final answer — retrying
    // it just delays telling the user to ask for a new invitation.
    retry: false
  })

  const { mutateAsync: acceptInvite, isPending } = useMutation({
    ...authorizationV2InviteAcceptCreateMutation(),
    onSuccess: () => {
      message.success(t('invitation_accepted'))
      // Accepting returns the user, not a session, so signing in is the next step.
      navigate(paths.auth.loginEmail, { state: { username: invite?.email } })
    },
    onError: (e) => showApiErrors(e, form)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()

    await acceptInvite({
      body: {
        invite_token: token,
        first_name: values.first_name,
        last_name: values.last_name,
        // `confirm_password` never leaves the browser — the API takes one password.
        password: values.password
      }
    })
  }

  const signInButton = (
    <Button type="primary" onClick={() => navigate(paths.auth.login)}>
      {t('sign_in')}
    </Button>
  )

  if (!token) {
    return (
      <Result
        status="warning"
        title={t('invitation_missing_token_title')}
        subTitle={t('invitation_missing_token_description')}
        extra={signInButton}
      />
    )
  }

  if (isLoading) {
    return <ContentSpinner />
  }

  if (isError || !invite) {
    return (
      <Result
        status="error"
        title={t('invitation_invalid_title')}
        subTitle={t('invitation_invalid_description')}
        extra={signInButton}
      />
    )
  }

  const organization = invite.organization_name

  if (invite.has_password) {
    return (
      <Flex orientation="vertical" gap={16} className="w-full">
        <AuthHeader
          title={t('invitation_title', { organization })}
          subtitle={t('invitation_existing_account')}
        />

        <Button
          block
          size="large"
          type="primary"
          onClick={() => navigate(paths.auth.loginPassword)}
        >
          {t('invitation_sign_in_to_join')}
        </Button>
      </Flex>
    )
  }

  return (
    <Flex orientation="vertical" gap={16} className="w-full">
      <AuthHeader
        title={t('invitation_title', { organization })}
        subtitle={t('invitation_subtitle')}
      />

      <Text type="secondary">
        {t('invitation_email_hint', { email: invite.email })}
      </Text>

      {errorAlert}

      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        requiredMark={false}
        id="invitation-form"
      >
        <Flex orientation="vertical" gap={16}>
          <Flex orientation="vertical" gap={10}>
            <Form.Item
              name="first_name"
              label={t('first_name')}
              style={{ marginBottom: 0 }}
              rules={[{ required: true }]}
            >
              <Input autoComplete="given-name" autoFocus />
            </Form.Item>

            <Form.Item
              name="last_name"
              label={t('last_name')}
              style={{ marginBottom: 0 }}
              rules={[{ required: true }]}
            >
              <Input autoComplete="family-name" />
            </Form.Item>

            <Form.Item
              name="password"
              label={t('password')}
              style={{ marginBottom: 0 }}
              rules={[
                { required: true },
                { min: 8, message: t('password_min_length') }
              ]}
            >
              <Input.Password
                placeholder={t('input_placeholder_password')}
                autoComplete="new-password"
              />
            </Form.Item>

            <Form.Item
              name="confirm_password"
              label={t('password_repeat')}
              dependencies={['password']}
              hasFeedback
              style={{ marginBottom: 0 }}
              rules={[
                { required: true },
                { min: 8, message: t('password_min_length') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve()
                    }

                    return Promise.reject(new Error(t('passwords_must_match')))
                  }
                })
              ]}
            >
              <Input.Password
                placeholder={t('input_placeholder_password')}
                autoComplete="new-password"
              />
            </Form.Item>
          </Flex>

          <Button
            block
            size="large"
            type="primary"
            onClick={onSubmit}
            loading={isPending}
            form="invitation-form"
            htmlType="submit"
          >
            {t('invitation_accept')}
          </Button>
        </Flex>
      </Form>

      <Flex align="center" justify="center" gap={6} wrap="wrap">
        <Text type="secondary">{t('have_account')}</Text>
        <AuthLink to={paths.auth.loginPassword}>{t('sign_in')}</AuthLink>
      </Flex>
    </Flex>
  )
}
